import useAuth from "./hooks/useAuth";
import { useEffect, useState, useCallback, useRef } from "react";
import IntonationResultsDisplay from './IntonationResultsDisplay';
import IntonationSnapshotView from './IntonationSnapshotView';

const CheckUserIntonation = ({ language, date, checkuser, representation }) => {
    console.log(language, date, checkuser, representation, "checkuserintonation")
    const { auth, setAuth } = useAuth();
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [autoTriggered, setAutoTriggered] = useState(false);
    const [forceMode, setForceMode] = useState(false);
    const [hasExistingData, setHasExistingData] = useState(false);
    const refreshTimeoutRef = useRef();
    
    // Effect for initial load - check for existing data instead of auto-triggering
    useEffect(() => { 
        if (language && representation === 'Comparison' && !autoTriggered) {
            checkExistingData();
            setAutoTriggered(true);
        }
    }, [language, representation]);

    // Handle date changes for Snapshot view
    useEffect(() => {
        if (representation === 'Snapshot' && date && date !== 'All records' && date !== 'Select date') {
            // Snapshot view doesn't need auto-trigger, it will fetch from intonation_results
            setAutoTriggered(true);
        }
    }, [date, representation]);

    // Check if there's existing data without triggering analysis
    const checkExistingData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const query = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/intonation_results?checkuser=${checkuser}`;

            const response = await fetch(query, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.results && data.results.length > 0) {
                    setHasExistingData(true);
                    // Format the data to match what the results display expects
                    setResults({
                        processed_files: data.results.length,
                        results: data.results,
                        force_mode: false
                    });
                } else {
                    setHasExistingData(false);
                }
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error checking existing data:", error);
            setHasExistingData(false);
        } finally {
            setLoading(false);
        }
    };

    const triggerIntonationComparison = async (force = false) => {
        if (representation !== 'Comparison') return;
        
        setLoading(true);
        setResultsLoading(true);
        if (force) {
            setForceMode(true);
        }
        
        try {
            const token = localStorage.getItem("token");
            let query = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/intonation_comparison`;
            
            const params = new URLSearchParams();
            if (checkuser) params.append('checkuser', checkuser);
            if (force) params.append('force', 'true');
            
            if (params.toString()) {
                query += `?${params.toString()}`;
            }

            const response = await fetch(query, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const json = await response.json();
                setResults(json);
                setHasExistingData(true);
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error triggering intonation comparison:", error);
        } finally {
            setLoading(false);
            setResultsLoading(false);
        }
    };

    const handleRefresh = useCallback(() => {
        if (representation === 'Comparison') {
            triggerIntonationComparison(false); // Normal refresh
        }
    }, [representation]);

    const handleForceRefresh = useCallback(() => {
        if (representation === 'Comparison') {
            triggerIntonationComparison(true); // Force refresh
        }
    }, [representation]);

    // Show different content based on representation
    if (representation === 'Snapshot') {
        return <IntonationSnapshotView language={language} date={date} checkuser={checkuser} />;
    }

    // Show loading state when checking for existing data
    if (loading && !results && !hasExistingData) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Checking for existing analysis data...</p>
                </div>
            </div>
        );
    }

    // Show prompt to start analysis when no data exists
    if (!hasExistingData && !results) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-2">🎵</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Intonation Analysis Data</h3>
                <p className="text-gray-500 mb-6">Start your first intonation analysis to compare your pronunciation with narrator references.</p>
                <div className="flex gap-3 justify-center">
                    <button
                        onClick={() => triggerIntonationComparison(false)}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                        Start First Analysis
                    </button>
                </div>
            </div>
        );
    }

    // Show loading state during analysis
    if (loading && !results) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">
                        {forceMode ? 'Force re-analyzing intonation...' : 'Processing intonation comparison...'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                        {forceMode 
                            ? 'Re-analyzing all files including previously processed ones' 
                            : 'Analyzing user files against narrator references'
                        }
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-full space-y-8">
            {/* Header for Comparison view */}
            <div className="text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <h2 className="text-2xl sm:text-3xl font-bold text-blue-700">
                    Intonation Comparison Analysis
                </h2>
                <p className="text-gray-600 mt-2">
                    {hasExistingData ? 'View and analyze existing pronunciation data' : 'Process and compare user pronunciation with narrator reference files'}
                </p>
                <div className="flex flex-wrap justify-center gap-3 mt-4">
                    <div className="bg-white px-3 py-1 rounded-full border border-blue-200 text-sm text-blue-700">
                        Language: {language}
                    </div>
                    <div className="bg-white px-3 py-1 rounded-full border border-blue-200 text-sm text-blue-700">
                        Processed: {results.processed_files}
                    </div>
                    {results.skipped_files > 0 && (
                        <div className="bg-white px-3 py-1 rounded-full border border-yellow-200 text-sm text-yellow-700">
                            Skipped: {results.skipped_files}
                        </div>
                    )}
                    <div className="bg-white px-3 py-1 rounded-full border border-blue-200 text-sm text-blue-700">
                        User: {checkuser}
                    </div>
                    <div className={`px-3 py-1 rounded-full border text-sm ${
                        results.force_mode 
                            ? 'bg-orange-100 border-orange-200 text-orange-700' 
                            : 'bg-green-100 border-green-200 text-green-700'
                    }`}>
                        Mode: {results.force_mode ? 'Forced' : 'Normal'}
                    </div>
                </div>
            </div>

            {/* Action Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Analysis Controls</h3>
                        <p className="text-gray-600 text-sm">
                            {hasExistingData 
                                ? `Found ${results.processed_files} existing analysis records`
                                : 'No existing data found - start your first analysis'
                            }
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleRefresh}
                            disabled={resultsLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {resultsLoading && !forceMode ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    {hasExistingData ? 'Re-run Analysis' : 'Start Analysis'}
                                </>
                            )}
                        </button>
                        {hasExistingData && (
                            <button
                                onClick={handleForceRefresh}
                                disabled={resultsLoading}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {resultsLoading && forceMode ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Forcing...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 4v5h-.582m-15.356 2A8.001 8.001 0 0119.418 9M19.418 9H15" />
                                        </svg>
                                        Force Re-run
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
                
                {/* Mode Explanation */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="flex items-start gap-3">
                            <div className="w-3 h-3 bg-blue-500 rounded-full mt-1 flex-shrink-0"></div>
                            <div>
                                <div className="font-medium text-gray-800">
                                    {hasExistingData ? 'Normal Analysis' : 'First Analysis'}
                                </div>
                                <div className="text-gray-600 mt-1">
                                    {hasExistingData 
                                        ? 'Only processes new files that haven\'t been analyzed before. Faster and avoids redundant work.'
                                        : 'Process all user files against narrator references to create initial analysis data.'
                                    }
                                </div>
                            </div>
                        </div>
                        {hasExistingData && (
                            <div className="flex items-start gap-3">
                                <div className="w-3 h-3 bg-orange-500 rounded-full mt-1 flex-shrink-0"></div>
                                <div>
                                    <div className="font-medium text-gray-800">Force Re-run</div>
                                    <div className="text-gray-600 mt-1">
                                        Re-analyzes all files, including previously processed ones. Use when you want fresh analysis.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Results Display */}
            {results && (
                <IntonationResultsDisplay 
                    results={results} 
                    loading={resultsLoading} 
                    language={language}
                    checkuser={checkuser}
                    forceMode={results.force_mode}
                />
            )}
        </div>
    );
}

export default CheckUserIntonation;